import googleTagManager from '@analytics/google-tag-manager';
import mixpanelPlugin from '@analytics/mixpanel';
import Analytics from 'analytics';
import moment from 'moment-timezone';
import getConfig from 'next/config';
import Router, { NextRouter } from 'next/router';
import { getCookiesSecured, SIGNUP_COOKIE } from 'utils/authUtils';
import { getRawCookie, setCookie } from 'utils/cookies';
import { isBrowser } from 'utils/domUtils';
import { navigateToUrl } from 'utils/navigateUtils';
import { setLimitedInterval } from 'utils/timerUtils';
import { parseQueryString } from 'utils/urlUtils';

const { publicRuntimeConfig } = getConfig();

const { NEXT_PUBLIC_GTM_ID, NEXT_PUBLIC_MIXPANEL_ID } = publicRuntimeConfig;

let analytics;

export function initAnalytics() {
  if (!isBrowser()) {
    return;
  }

  if (!analytics && (NEXT_PUBLIC_GTM_ID?.trim() || NEXT_PUBLIC_MIXPANEL_ID?.trim())) {
    analytics = Analytics({
      app: 'USREM',
      plugins: [
        ...(NEXT_PUBLIC_GTM_ID?.trim()
          ? [
              googleTagManager({
                containerId: NEXT_PUBLIC_GTM_ID?.trim()
              })
            ]
          : []),
        ...(NEXT_PUBLIC_MIXPANEL_ID?.trim()
          ? [
              mixpanelPlugin({
                token: NEXT_PUBLIC_MIXPANEL_ID?.trim()
              })
            ]
          : [])
      ]
    });
  }
}

export function identify(
  userId?: string,
  name?: string,
  email?: string,
  user_type?: string,
  is_demo?: boolean
) {
  if (!isBrowser()) {
    return;
  }

  if (!userId) {
    return;
  }

  console.info('identify', userId, name, email, user_type, is_demo);

  // Check if Clarity has been initialized before calling its methods
  setLimitedInterval(
    () => {
      if (typeof window['clarity'] === 'function') {
        window['clarity']('identify', userId);
        window['clarity']('set', 'name', name);
        window['clarity']('set', 'email', email);
        window['clarity']('set', 'user_type', user_type);
        window['clarity']('set', 'is_demo', String(!!is_demo));
        return true;
      }
    },
    1000,
    5
  );

  if (analytics) {
    analytics.identify(userId, {
      name: name,
      email: email,
      user_type: user_type,
      is_demo: String(!!is_demo)
    });
  }
}

export function uninitGtm() {
  analytics = undefined;
}

export function logModalView(title?: string) {
  return reportEvent('modal_open', {
    title: title
  });
}

export function logPageView(url?: string) {
  if (analytics) {
    const urlStr = url || Router?.asPath;
    if (urlStr) {
      const urlParts = urlStr.split('?');
      const queryParams = parseQueryString(urlParts[1]);
      reportEvent(urlParts[0], { ...queryParams, category: 'page_view' });
    }
  }
}

export function logEvent(
  action: string,
  category?: string,
  label?: string,
  nonInteraction = false
) {
  event(action, label, category, nonInteraction);
}

export function reportEventAndNavigate(action: string, url?: string) {
  reportEvent(action, {
    url: url,
    event_callback: () => url && navigateToUrl(url)
  });
}

function event(action: string, label?: string, category?: string, nonInteraction = false) {
  return reportEvent(action, {
    event_label: label,
    event_category: category,
    non_interaction: nonInteraction
  });
}

export function reportEvent(action: string, params?: Record<string, unknown>) {
  if (analytics) {
    console.log('reportEvent', action, params);
    analytics.track(action, params);
  }
}

export const updateCampaignCookie = (router?: NextRouter) => {
  const queryParams = router?.query;
  if (!!queryParams?.sponsor || !!queryParams?.utm_campaign) {
    setCookie(SIGNUP_COOKIE, queryParams, {
      expires: moment().add(14, 'days').toDate().toUTCString(),
      path: '/',
      secure: getCookiesSecured()
    });
  } else {
    const gtmCookie = getRawCookie('__gtm_campaign_url');
    if (gtmCookie) {
      const gtmCookieParams = parseQueryString(decodeURIComponent(gtmCookie)?.split('?')[1]);
      if (!!gtmCookieParams?.sponsor || !!gtmCookieParams?.utm_campaign) {
        setCookie(SIGNUP_COOKIE, gtmCookieParams, {
          expires: moment().add(14, 'days').toDate().toUTCString(),
          path: '/',
          secure: getCookiesSecured()
        });
      }
    }
  }
};
